// T7 (agent-bridge), file-size split (CLAUDE.md «Контроль размера файлов»).
//
// `storeHelpers.ts` crossed the 2000-line ceiling after an origin/main merge
// (unrelated GRIPS/BEDIT work) landed on top of this branch's own necessary
// 2-line `header/rename-layer` routing addition, pushing an already-at-ceiling
// file (2000 lines on origin/main) to 2002. `resolveGeometryDocumentCommand`
// was the single largest self-contained function in the file (~580 lines, the
// dispatch-router allowlist deciding which commands reach the geometry
// reducer vs. fall through to the audit-only shell reducer) — extracted here
// WHOLE (verbatim body, only the `cachedEntitiesLookup` module-state read
// changed to the `getCachedEntitiesLookup()` accessor — see
// `cachedEntitiesRegistry.ts`'s header comment for why that split was needed
// too, to avoid an import cycle between this file and storeHelpers.ts).
//
// `storeHelpers.ts` re-exports this function so every existing
// `import { resolveGeometryDocumentCommand } from "./storeHelpers"` call site
// keeps working unchanged (barrel re-export, no external import-path churn).
import {
  type DrawingDocument,
} from "@dxf-ai-editor/drawing-model";
import {
  type DynamicBlockCommand,
  type EditorCommand,
  type EditorShellState,
} from "@dxf-ai-editor/contracts";
// MATCHPROP store-side resolution seams (@codex P2-A/P2-C #420) — the source
// resolver + persistence gate injected into the pure resolution helper.
import { buildMatchpropResolutionDeps } from "./matchpropStoreDeps";
import {
  resolveGeometryCommandFromText,
  type GeometryCreateCommand,
} from "./geometryReducer";
import {
  parseLayoutMutationText,
  resolveLayoutMutationCommand,
} from "./spaceMutationGuards";
// FINDING 4 — the COLOR-SET value parser (strict) shared with the App resolver.
// Pure (drawing-model + i18n only — no back-edge to state/), so importing it
// here keeps the dep-graph acyclic while routing COLOR-SET through the SAME seam
// as `executeBatchCommands` / AI text submissions (one truth).
import { parseColorSetCommand } from "../dynamicInput/colorCommand";
import { parseVpclipCommandText } from "../dynamicInput/vpclipCommand";
// MATCHPROP → `properties/apply` resolution (@codex P1 #420), extracted to its
// own module (file-size policy).
import { resolveMatchpropApplyToPropertiesApply } from "./matchpropResolution";
import { getCachedEntitiesLookup } from "./cachedEntitiesRegistry";

export function resolveGeometryDocumentCommand(
  state: EditorShellState,
  command: EditorCommand | DynamicBlockCommand,
  // EPIC-COLOR-UNION S1 round-2 (leak-hunt P2) — the pre-mutation geometry
  // DOCUMENT. The CLI `MATCHPROP` arm below bakes the source entity's `Color`
  // into a `properties/apply`; reading it from the LOSSY `EntityPreview`
  // property bag ("#rrggbb"/"ByLayer" from `dxfGeometry.ts:60`) dropped a rich
  // source colour's 62/430 siblings onto every target (and collapsed a
  // bookColor source to ByLayer) — the SAME leak class P2-A/B/C fixed on the
  // `properties/apply` apply + undo + explode paths. With the document in hand
  // we source the source colour LOSSLESSLY (`serializeColor(coerceColor(...))`)
  // so the apply path restores the full union on the target via
  // `commonFieldPatchValue → deserializeColor`. Optional so the (test-only)
  // callers that omit it keep the legacy preview read.
  geometryDocument?: DrawingDocument,
):
  | GeometryCreateCommand
  | Extract<
      EditorCommand,
      {
        type:
          | "properties/apply"
          | "shell/delete-entities"
          // LAYOUT-COMMAND-HANDLERS — space lifecycle (LAYOUT NEW /
          // DELETE / RENAME). Routed so the geometry document's
          // `spaces[]` + `layouts` page-setup dicts actually mutate
          // (the shell arm is a projection the resync overwrites).
          | "shell/add-space"
          | "shell/remove-space"
          | "shell/rename-space"
          // Phase 4.1.c.2 — single-entity delete for undo/redo.
          | "geometry/delete-entity"
          // Phase 4.1.c.2 follow-up (P1-3/P1-4/P1-5) — history-inverse
          // restore commands emitted by computeInverse.
          | "geometry/restore-entity"
          | "shell/restore-entities"
          // PR #324 / #318 R2 — in-place viewport-clip restore, the inverse
          // half of a delete that cleared a dangling viewport clip. Emitted by
          // computeInverse inside a history/replay-inverses batch.
          | "geometry/restore-viewport-clip"
          // @codex PR #785 round 2, F2 — the PRESENCE-BIT half of the
          // `properties/apply` inverse: an in-place full-snapshot REPLACE of an
          // existing entity, emitted by the editorStore inverse composition
          // inside a history/replay-inverses batch.
          | "geometry/restore-entity-snapshot"
          | "geometry/add-leader-to-multileader"
          | "geometry/remove-leader-from-multileader"
          | "geometry/update-multileader-vertex"
          | "geometry/update-multileader-text-location"
          | "geometry/update-multileader-text-rotation"
          | "geometry/update-multileader-dogleg"
          | "geometry/remove-multileader-vertex"
          | "geometry/add-multileader-vertex"
          | "geometry/create-block-definition"
          // history-internal undo inverse of create-block-definition.
          | "geometry/remove-block-definition"
          | "geometry/explode-insert"
          | "geometry/add-dynamic-parameter"
          | "geometry/update-dynamic-parameter"
          | "geometry/remove-dynamic-parameter"
          | "geometry/add-dynamic-action"
          | "geometry/update-dynamic-action"
          | "geometry/remove-dynamic-action"
          | "geometry/add-visibility-state"
          | "geometry/rename-visibility-state"
          | "geometry/delete-visibility-state"
          | "geometry/reorder-visibility-states"
          | "geometry/set-default-visibility-state"
          | "geometry/toggle-visibility-state-entity"
          // Phase K.4 — Visibility States Manager additions
          | "geometry/set-current-visibility-state"
          | "geometry/bvhide"
          | "geometry/bvshow"
          | "geometry/bvshow-all"
          | "geometry/create-lookup-table"
          | "geometry/update-lookup-table"
          | "geometry/delete-lookup-table"
          | "geometry/set-block-properties-table"
          | "geometry/add-block-property"
          | "geometry/update-block-property"
          | "geometry/remove-block-property"
          | "geometry/reorder-block-properties"
          | "geometry/copy"
          // FIX-4 Cluster C / D — modify commands that mutate geometry
          // document; routed here so reduceGeometryDocument actually runs.
          | "geometry/move-entities"
          | "geometry/mirror-entities"
          | "geometry/rotate-entities"
          | "geometry/scale-entities"
          | "geometry/offset-entity"
          | "geometry/stretch-entities"
          | "geometry/trim-entity"
          | "geometry/extend-entity"
          | "geometry/fillet-entities"
          | "geometry/chamfer-entities"
          | "geometry/break-entity"
          | "geometry/join-entities"
          | "geometry/explode-entities"
          | "geometry/lengthen-entity"
          | "file/purge-blocks"
          | "file/rename-block"
          // XREF suite (Phase FIN.3) — the geometry reducer owns the actual
          // xrefRegistry / block-definition mutation (xattachMutation,
          // xclipMutation, …). Without routing here the shell reducer only logs
          // an audit event and the document never changes — so an xref edit was
          // invisible to the document, undo, AND the revision counters (ABB-S1
          // G3 P1). Routed so the registry/block-def mutation actually applies.
          | "file/xattach"
          | "file/xbind"
          | "file/xunload"
          | "file/xreload"
          | "file/xdetach"
          | "file/xclip"
          // ABB-S1 round-3 F2/F3 — the history-internal SNAPSHOT-RESTORE inverse
          // of every `file/x*` command (computeInverse → geometry reducer
          // `file/xref-restore-snapshot` case). Without routing here undo/redo of
          // an xref edit silently no-ops (the shell reducer is audit-only).
          | "file/xref-restore-snapshot"
          // Phase FIN.2 — annotative commands. Returned to the dispatch
          // pipeline so `reduceGeometryDocument` actually runs and the
          // document's CANNOSCALE / annotation state mutates. See the
          // annotative branch in the body for the full inventory.
          | "header/set-cannoscale"
          // CECOLOR / COLOR / ЦВЕТ — $CECOLOR current-entity-colour. Routed so
          // `reduceGeometryDocument` runs `applySetCeColor` and `doc.cecolor`
          // mutates (the SET side of CECOLOR; without it COLOR + its undo no-op).
          | "header/set-cecolor"
          // PR-6 — `header/set-cannoscale-and-sync` is the ANNOAUTOSCALE-
          // materializing variant of `header/set-cannoscale`. Same
          // routing requirement as the plain set-cannoscale: without
          // explicit routing here the geometry reducer never runs and
          // the materialization pass is silently dropped (the shell
          // reducer's needsShellResyncFromGeometry allowlist re-derives
          // shell entities from an unmodified document — visible scene
          // unchanged despite dropdown selection).
          | "header/set-cannoscale-and-sync"
          | "geometry/annoupdate"
          | "geometry/objectscale-add"
          | "geometry/objectscale-remove"
          // PR-7 — universal per-entity OBJECTSCALE (works for any
          // annotative-capable entity, not just INSERT). Same routing
          // requirement as the legacy objectscale-add/-remove above —
          // without explicit routing here, the geometry reducer never
          // runs and the entity's annotativeContexts[] never updates.
          | "entity/add-annotation-context"
          | "entity/remove-annotation-context"
          // PR-4 — annotation status-bar toggles. Routed to the
          // geometry reducer so the header booleans (annoAllVisible /
          // annoMonitor) and the int annoAutoScale actually mutate.
          // Without this routing the dispatched commands silently no-op
          // (the shell reducer only emits an audit event for them) —
          // identical regression vector as PR-3 fixer #2.
          | "header/toggle-annoallvisible"
          | "header/toggle-annoautoscale"
          | "header/toggle-annomonitor"
          // PR-9 — SCALELISTEDIT scale-list catalogue mutations. Routed to the
          // geometry reducer so `header.annotationScales[]` mutates (else the
          // dialog Add/Delete silently no-op as an audit-only shell event).
          | "header/scalelist-add"
          | "header/scalelist-remove"
          // Phase 5 (entity-remap delete/rename) + Lane J (D3-1: the eight
          // direct-set → dispatch add/update/plot commands + their snapshot-
          // restore undo inverse) — the whole style/layer TABLE + layout-plot
          // family, routed so `styleTablesReducer.ts` runs in the dispatch
          // pipeline (undo-able edits; without it they fall through to the
          // audit-only shell reducer and styleTables/layouts stay frozen).
          | "header/delete-dimension-style"
          | "header/rename-dimension-style"
          | "header/delete-multileader-style"
          | "header/delete-layer"
          | "header/rename-layer" // T7 (agent-bridge) — else no-ops (audit-only)
          | "header/add-dimension-style"
          | "header/update-dimension-style"
          | "header/add-multileader-style"
          | "header/update-multileader-style"
          | "header/update-text-style"
          | "header/add-layer"
          | "header/update-layer"
          | "header/set-layout-plot-settings"
          | "header/restore-style-tables-snapshot"
          // Phase MC5 viewport mutation commands (VPCLIP/VPLAYER/MVSETUP/LOCK/SHADEPLOT) — without routing the reducer no-ops (audit-only).
          | "geometry/viewport-set-lock"
          | "geometry/viewport-set-shadeplot"
          | "geometry/viewport-vplayer"
          | "geometry/viewport-clip-set"
          | "geometry/viewport-clip-set-polygon"
          | "geometry/viewport-clip-remove"
          | "geometry/viewport-set-scale"
          | "geometry/viewport-set-view";
      }
    >
  | DynamicBlockCommand
  | null {
  // @codex P2-A/P2-C (#420) — the SHARED MATCHPROP resolution seams used by BOTH
  // the typed `matchprop/apply` router below AND the CLI `MATCHPROP` text arm;
  // `cachedEntitiesLookup` threads the large-import non-reactive cache.
  const matchpropDeps = buildMatchpropResolutionDeps(
    state,
    geometryDocument,
    getCachedEntitiesLookup(),
  );
  // MATCHPROP persistence (@codex P1 #420) — a typed `matchprop/apply` (AI safe
  // contour + Inspector toolbar) resolves to the SAME `properties/apply` the CLI
  // `MATCHPROP` text arm produces, so the copied properties land in the geometry
  // DOCUMENT (undo history + affectedEntityIds + export/reload), not only the
  // preview shell reducer.
  if (command.type === "matchprop/apply") {
    return resolveMatchpropApplyToPropertiesApply(
      state,
      command.source,
      command.mode,
      command.targetEntityIds,
      geometryDocument,
      matchpropDeps,
    );
  }
  if (
    command.type === "geometry/create-line" ||
    command.type === "geometry/create-point" ||
    command.type === "geometry/create-circle" ||
    command.type === "geometry/create-arc" ||
    command.type === "geometry/create-ellipse" ||
    command.type === "geometry/create-spline" ||
    command.type === "geometry/create-lwpolyline" ||
    command.type === "geometry/create-donut" ||
    command.type === "geometry/create-text" ||
    command.type === "geometry/create-mtext" ||
    command.type === "geometry/create-dimension" ||
    command.type === "geometry/create-multileader" ||
    command.type === "geometry/add-leader-to-multileader" ||
    command.type === "geometry/remove-leader-from-multileader" ||
    command.type === "geometry/update-multileader-vertex" ||
    command.type === "geometry/update-multileader-text-location" ||
    command.type === "geometry/update-multileader-text-rotation" ||
    command.type === "geometry/update-multileader-dogleg" ||
    command.type === "geometry/remove-multileader-vertex" ||
    command.type === "geometry/add-multileader-vertex" ||
    command.type === "geometry/create-revcloud" ||
    command.type === "geometry/create-wipeout" ||
    command.type === "geometry/create-hatch" ||
    command.type === "geometry/create-block-insert" ||
    // T6b — AI-dispatch coalescing batch sibling of create-block-insert (see
    // the reducer case + `aiSafeContour.ts`'s coalescer).
    command.type === "geometry/create-block-inserts-batch" ||
    // CECOLOR (EPIC-COLOR-UNION round-6 FINDING A) — ATTDEF authoring inside
    // BEDIT (toolbar `useAttributeDialogs` + AI `blockDispatcherAttribute`).
    // Routing it here is the CORRECT seam: it flows through the dispatch
    // chokepoint (editorStore.dispatch → stampCeColorOntoCreateCommand →
    // reduceGeometryDocumentWithMutations → handleCreateAttdef) exactly like
    // the other create-* / dynamic-block-authoring commands. Without this
    // branch `resolvedGeometryCommand` was null, so neither the round-5
    // reducer-stamp nor the geometry reducer's create-attdef case was ever
    // reached via the store — the stamp was effectively DEAD. The reducer +
    // BEDIT mirror (syncBlockEditSpaceToDefinition) are already in place.
    command.type === "geometry/create-attdef" ||
    // CECOLOR round-6 FINDING B — history-internal undo inverse of
    // `geometry/create-attdef`. `computeInverse` (editorStore) emits this so
    // UNDO removes the freshly-authored ATTDEF from `blocks[name].entities`.
    // Without routing here the inverse never reaches the geometry reducer's
    // `case "geometry/remove-attdef"` (line ~1903) → undo silently no-ops and
    // the ATTDEF lingers. Mirrors the `remove-block-definition` inverse below.
    command.type === "geometry/remove-attdef" ||
    // round-9 FINDING B — BATTMAN ATTDEF reorder (Move Up/Down, useBattmanDialog
    // → dispatch). Same routing gap as create-attdef was: the shell reducer only
    // logs an audit event, so without this branch `resolvedGeometryCommand` is
    // null and the geometry reducer's `case "geometry/reorder-attdefs"` (→
    // handleReorderAttdefs) never runs through the store — the user's Move Up/Down
    // silently no-ops (the permuted order never reaches blocks[name].entities) and
    // no history is recorded. Routing it here flows it through the dispatch
    // chokepoint exactly like create-attdef / remove-attdef.
    command.type === "geometry/reorder-attdefs" ||
    // Lane F/D2 discovery — DDATTE / ATTEDIT / EATTEDIT ATTRIB value edits
    // (useAttributeDialogs.ts `dispatch({type: "geometry/update-attribute-
    // value(s)", ...})`) hit the SAME routing gap create-attdef / remove-attdef
    // / reorder-attdefs above were fixed for: the shell reducer only logs an
    // audit event, so without this branch `resolvedGeometryCommand` is null and
    // the geometry reducer's `case "geometry/update-attribute-value"` /
    // `case "geometry/update-attribute-values"` (→ handleUpdateAttributeValue /
    // handleUpdateAttributeValues) never run through the store — an ATTRIB edit
    // silently no-ops (the dialog closes, nothing persists, no history entry),
    // discovered while building the S10c ATTRIB-render visual gate (this PR's
    // scenario needed the substituted value to actually reach the document +
    // re-flatten). Routed here exactly like its ATTDEF siblings.
    command.type === "geometry/update-attribute-value" ||
    command.type === "geometry/update-attribute-values" ||
    command.type === "geometry/create-block-definition" ||
    // history-internal undo inverse of create-block-definition — route to the
    // geometry reducer so blocks[name] / blockRecords[name] are actually removed
    // on undo (otherwise the inverse silently no-ops and the block lingers).
    command.type === "geometry/remove-block-definition" ||
    command.type === "geometry/explode-insert" ||
    command.type === "geometry/create-arrayrect" ||
    command.type === "geometry/create-arraypolar" ||
    command.type === "geometry/create-arraypath" ||
    command.type === "geometry/arrayedit" ||
    // HATCHEDIT — same routing as arrayedit (else the reducer case never runs).
    command.type === "geometry/hatchedit" ||
    command.type === "geometry/create-viewport" ||
    // AutoCAD COPY with base + second point payload. Needs routing to
    // the geometry reducer so the new clones actually land in the
    // document (the sibling `geometry/copy-entities` delta command
    // goes through a different path — audit-only in the shell
    // reducer, no document mutation; that pre-existing gap is left
    // alone here because the new base-point COPY flow replaces it).
    command.type === "geometry/copy" ||
    // FIX-4 Cluster C / D — modify commands that mutate the geometry
    // document directly. Without routing here the geometryReducer
    // never runs, the document stays unchanged, and the shell resync
    // (editorStore.ts:1547) simply rebuilds shell entities from the
    // unmutated document — the visible scene remains identical to
    // before dispatch despite the user clicking through the full
    // OFFSET / FILLET / MOVE / ROTATE / SCALE / MIRROR / STRETCH /
    // TRIM / EXTEND / CHAMFER / BREAK / JOIN / EXPLODE / LENGTHEN
    // flow. The shell sub-reducer for these commands is audit-only on
    // purpose; the document mutation is owned by `reduceGeometryDocument`
    // and must reach it through this router.
    command.type === "geometry/move-entities" ||
    command.type === "geometry/mirror-entities" ||
    command.type === "geometry/rotate-entities" ||
    command.type === "geometry/scale-entities" ||
    command.type === "geometry/offset-entity" ||
    command.type === "geometry/stretch-entities" ||
    command.type === "geometry/trim-entity" ||
    command.type === "geometry/extend-entity" ||
    command.type === "geometry/fillet-entities" ||
    command.type === "geometry/chamfer-entities" ||
    command.type === "geometry/break-entity" ||
    command.type === "geometry/join-entities" ||
    command.type === "geometry/explode-entities" ||
    command.type === "geometry/lengthen-entity" ||
    command.type === "properties/apply" ||
    command.type === "shell/delete-entities" ||
    // LAYOUT-COMMAND-HANDLERS — typed space lifecycle dispatches (LAYOUT
    // NEW / DELETE / RENAME from the tab strip, dialogs or programmatic
    // callers). Without routing here the geometry reducer never runs,
    // the document's `spaces[]` stays frozen, and the very next shell
    // resync visually REVERTS the add/remove/rename (the resync rebuilds
    // `state.drawing.spaces` from the unmodified document) — confirmed
    // live for shell/rename-space before this fix.
    command.type === "shell/add-space" ||
    command.type === "shell/remove-space" ||
    command.type === "shell/rename-space" ||
    // Phase 4.1.c.2 — single-entity delete for undo/redo inverse dispatch.
    // Without routing here, the geometry reducer never runs and the entity
    // is not actually removed from the document.
    command.type === "geometry/delete-entity" ||
    // Phase 4.1.c.2 follow-up (P1-3/P1-4/P1-5) — history-inverse restore
    // commands. These are emitted by computeInverse as the inverse of
    // geometry/delete-entity and shell/delete-entities respectively. Without
    // routing here the geometry reducer never runs and the entity is not
    // re-inserted into the document on undo.
    command.type === "geometry/restore-entity" ||
    command.type === "shell/restore-entities" ||
    // PR #324 / #318 R2 — in-place viewport-clip restore (inverse half of a
    // delete that cleared a dangling viewport clip). Without routing here the
    // geometry reducer never runs and the survivor's clip stays cleared on undo.
    command.type === "geometry/restore-viewport-clip" ||
    // @codex PR #785 round 2, F2 — PRESENCE-BIT snapshot restore for a
    // `properties/apply` undo. Without routing here the geometry reducer never
    // runs and the undo leaves the entity carrying the optional DXF group the
    // apply materialised (an explicit `40 0` on a POLYLINE that never had one).
    command.type === "geometry/restore-entity-snapshot" ||
    // Phase 2.7 / 2.8 — file-level block-table mutations. The shell
    // reducer only logs an audit event for these; without explicit
    // routing here the geometry document never sees the dispatch and
    // PURGE / RENAME silently no-op (audit success but blocks dict
    // unchanged, INSERT references stale, DXF export emits old names).
    command.type === "file/purge-blocks" ||
    command.type === "file/rename-block" ||
    // XREF suite (Phase FIN.3) — route to the geometry reducer so the
    // xrefRegistry / block-definition mutation (xattach/xbind/xunload/xreload/
    // xdetach/xclip) actually applies; the shell reducer is audit-only. Without
    // this an xref edit changed NOTHING in the document → invisible to undo and
    // the revision counters (ABB-S1 G3 P1).
    command.type === "file/xattach" ||
    command.type === "file/xbind" ||
    command.type === "file/xunload" ||
    command.type === "file/xreload" ||
    command.type === "file/xdetach" ||
    command.type === "file/xclip" ||
    // ABB-S1 round-3 F2/F3 — the history-internal SNAPSHOT-RESTORE inverse of
    // the xref family (see the union-type entry above for the rationale).
    command.type === "file/xref-restore-snapshot" ||
    // DYN-1 Tier 1 — dynamic block authoring commands. Same pattern:
    // shell reducer logs audit events; geometry reducer handles the
    // actual BlockDefinition.dynamicMetadata mutations. Without
    // routing here parameters/actions/visibility states never persist.
    command.type === "geometry/add-dynamic-parameter" ||
    command.type === "geometry/update-dynamic-parameter" ||
    command.type === "geometry/remove-dynamic-parameter" ||
    command.type === "geometry/add-dynamic-action" ||
    command.type === "geometry/update-dynamic-action" ||
    command.type === "geometry/remove-dynamic-action" ||
    command.type === "geometry/add-visibility-state" ||
    command.type === "geometry/rename-visibility-state" ||
    command.type === "geometry/delete-visibility-state" ||
    command.type === "geometry/reorder-visibility-states" ||
    command.type === "geometry/set-default-visibility-state" ||
    command.type === "geometry/toggle-visibility-state-entity" ||
    // Phase K.4 — Visibility States Manager additions
    command.type === "geometry/set-current-visibility-state" ||
    command.type === "geometry/bvhide" ||
    command.type === "geometry/bvshow" ||
    command.type === "geometry/bvshow-all" ||
    // DYN-2 B.2 — Lookup Tables + Block Properties Table commands.
    command.type === "geometry/create-lookup-table" ||
    command.type === "geometry/update-lookup-table" ||
    command.type === "geometry/delete-lookup-table" ||
    command.type === "geometry/set-block-properties-table" ||
    command.type === "geometry/add-block-property" ||
    command.type === "geometry/update-block-property" ||
    command.type === "geometry/remove-block-property" ||
    command.type === "geometry/reorder-block-properties" ||
    // DYNLIST / DYNSET / DYNRESET — INSERT customProperties CLI
    // (no evaluator re-run — storage-only mutation).
    command.type === "dynamic/list" ||
    command.type === "dynamic/set" ||
    command.type === "dynamic/reset" ||
    // Phase N.4 — per-INSERT representation override commands
    // (typed parameterOverrides + visibilityState mutations).
    command.type === "geometry/set-insert-parameter-override" ||
    command.type === "geometry/clear-insert-parameter-override" ||
    command.type === "geometry/set-insert-visibility-state" ||
    // Phase FIN.2 — annotative commands. Without routing here,
    // `header/set-cannoscale` (the CANNOSCALE status-bar selector) and
    // its sibling ANNOUPDATE / OBJECTSCALE commands never reach
    // `reduceGeometryDocument`, so the document's `cannoscaleName` /
    // `cannoscale` / per-INSERT `annotationScales` stay frozen and the
    // controlled `<select value={geometryDocument.cannoscaleName}>` in
    // App.tsx visibly snaps back to the previous selection. The shell
    // reducer's `needsShellResyncFromGeometry` allowlist already
    // includes these commands, but resync against an unmodified
    // document is a no-op — the geometry reducer must run first.
    // Bug confirmed live on АБИ1.dxf 2026-05-02: select onChange
    // dispatched, but selectValue stayed "1:200" and
    // `<g transform="matrix(100 0 0 100 ...)">` did not change after
    // switching to "1:10" (which the entity HAS a context for).
    command.type === "header/set-cannoscale" ||
    command.type === "header/set-cannoscale-and-sync" ||
    // BASE / БАЗА — $INSBASE drawing insertion base point. Route to the
    // geometry reducer so `doc.insertionBase` actually mutates (otherwise the
    // command never reaches reduceGeometryDocument and the field stays frozen).
    command.type === "header/set-insertion-base" ||
    // CECOLOR / COLOR / ЦВЕТ — $CECOLOR current-entity-colour. Same routing
    // requirement as BASE above: without this branch `header/set-cecolor` never
    // reaches `reduceGeometryDocument`, so `applySetCeColor` never runs,
    // `doc.cecolor` stays frozen at ByLayer, and `computeInverse` (which reads
    // the pre-mutation `doc.cecolor`) makes UNDO of a COLOR command a permanent
    // no-op. The CLI COLOR / ribbon swatch / AI all dispatch this command — all
    // were silently dropped here until this allowlist entry.
    command.type === "header/set-cecolor" ||
    command.type === "geometry/annoupdate" ||
    command.type === "geometry/objectscale-add" ||
    command.type === "geometry/objectscale-remove" ||
    // PR-7 — universal per-entity OBJECTSCALE routing. See union
    // expansion above for the rationale; without this branch the
    // geometry reducer never receives the dispatch and the entity's
    // annotativeContexts[] never mutates.
    command.type === "entity/add-annotation-context" ||
    command.type === "entity/remove-annotation-context" ||
    // PR-4 — AnnotationStatusBar toggle buttons (annoAllVisible /
    // annoAutoScale / annoMonitor). Same routing requirement as the
    // CANNOSCALE selector above — without explicit routing here the
    // header booleans never flip, the status-bar `data-active` state
    // snaps back to the prior value, and downstream visibility filters
    // (useVisibleEntities / cannoscaleVisibilityFilter) keep using the
    // stale flag.
    command.type === "header/toggle-annoallvisible" ||
    command.type === "header/toggle-annoautoscale" ||
    command.type === "header/toggle-annomonitor" ||
    // PR-9 — SCALELISTEDIT scale-list catalogue mutations (see union-expansion
    // comment above; without this branch `annotationScales[]` stays frozen).
    command.type === "header/scalelist-add" ||
    command.type === "header/scalelist-remove" ||
    // Phase 5 (entity-remap delete/rename) + Lane J (D3-1: the eight direct-set
    // → dispatch add/update/plot commands + their snapshot-restore inverse) —
    // the whole style/layer TABLE + layout-plot family, routed so
    // `styleTablesReducer.ts` runs in the dispatch pipeline (undo-able edits).
    command.type === "header/delete-dimension-style" ||
    command.type === "header/rename-dimension-style" ||
    command.type === "header/delete-multileader-style" ||
    command.type === "header/delete-layer" ||
    command.type === "header/rename-layer" || // T7 (agent-bridge)
    command.type === "header/add-dimension-style" ||
    command.type === "header/update-dimension-style" ||
    command.type === "header/add-multileader-style" ||
    command.type === "header/update-multileader-style" ||
    command.type === "header/update-text-style" ||
    command.type === "header/add-layer" ||
    command.type === "header/update-layer" ||
    command.type === "header/set-layout-plot-settings" ||
    command.type === "header/restore-style-tables-snapshot" ||
    // Phase MC5 viewport mutation commands — same routing requirement: without allowlisting here the geometry reducer never runs (audit-only shell no-op).
    command.type === "geometry/viewport-set-lock" ||
    command.type === "geometry/viewport-set-shadeplot" ||
    command.type === "geometry/viewport-vplayer" ||
    command.type === "geometry/viewport-clip-set" ||
    command.type === "geometry/viewport-clip-set-polygon" ||
    command.type === "geometry/viewport-clip-remove" ||
    command.type === "geometry/viewport-set-scale" ||
    command.type === "geometry/viewport-set-view"
  ) {
    return command as ReturnType<typeof resolveGeometryDocumentCommand>;
  }

  if (command.type !== "commandline/submit") {
    return null;
  }

  // ── LAYOUT NEW / DELETE (LAYOUT-COMMAND-HANDLERS) ───────────────────
  // Space lifecycle mutates the geometry DOCUMENT (the source of truth
  // the shell resync rebuilds `state.drawing.spaces` from), so the
  // commandline text must resolve to the typed shell command HERE too —
  // exactly the same parse + deterministic resolution
  // (`spaceMutationGuards.ts`) that `routeLayoutCommand` performs for
  // the shell arm, evaluated against the same pre-dispatch state, so
  // both arms mint the SAME space id / resolve the SAME target.
  // Resolution failures (unknown name / Model default) return null —
  // the shell arm owns the warning audit.
  const layoutMutationParse = parseLayoutMutationText(command.text);
  if (layoutMutationParse) {
    const resolution = resolveLayoutMutationCommand(
      state.drawing.spaces,
      state.drawing.activeSpaceId,
      layoutMutationParse,
    );
    if (!resolution.ok) return null;
    return { ...resolution.command, source: command.source };
  }

  // ── COLOR / -COLOR / SETCOLOR / ЦВЕТ <value> (FINDING 4) ────────────
  // The COLOR-SET VALUE form is resolved at the SHARED store seam so the
  // store-level `executeBatchCommands` + AI text paths route through the SAME
  // parse as the interactive `App.submitCommandText`, all converging on the
  // invertible `header/set-cecolor`. `parseColorSetCommand` returns null for a
  // bare COLOR (no-value picker/prompt forms are UI-only) → falls through.
  const colorSetParse = parseColorSetCommand(command.text);
  if (colorSetParse) {
    return {
      type: "header/set-cecolor",
      source: command.source,
      color: colorSetParse,
    } as ReturnType<typeof resolveGeometryDocumentCommand>;
  }

  const chpropCommandlineMatch = /^CHPROP\s+([^=]+?)\s*=\s*(.+)$/i.exec(
    command.text.trim(),
  );
  if (chpropCommandlineMatch && state.selection.entityIds.length > 0) {
    const propertyName = (chpropCommandlineMatch[1] ?? "")
      .trim()
      .replace(/_/g, " ");
    return {
      type: "properties/apply",
      source: command.source,
      entityIds: state.selection.entityIds,
      values: { [propertyName]: (chpropCommandlineMatch[2] ?? "").trim() },
    };
  }

  const matchpropCommandlineMatch =
    /^MATCHPROP(?:\s+(BASIC|SPECIAL|ALL))?$/i.exec(command.text.trim());
  if (matchpropCommandlineMatch) {
    const mode =
      matchpropCommandlineMatch[1]?.toLowerCase() === "special"
        ? "special"
        : matchpropCommandlineMatch[1]?.toLowerCase() === "basic"
          ? "basic"
          : "all";
    // Shared one-truth resolution (@codex P1 #420) — identical to the typed
    // `matchprop/apply` router above; a `null` result falls through to normal
    // geometry resolution (same P2-A/P2-C seams).
    const resolved = resolveMatchpropApplyToPropertiesApply(
      state,
      command.source,
      mode,
      state.selection.entityIds,
      geometryDocument,
      matchpropDeps,
    );
    if (resolved) return resolved;
  }

  // VPCLIP / ВЭКРЕЗ <vp> [P pts | D | O objId | pts] (@codex #496 P2) — resolve
  // the SCRIPTED VPCLIP form at the SHARED store seam so a
  // `commandline/submit` VPCLIP (executeBatchCommands / AI text) routes through
  // the SAME pure parser as the interactive `submitCommandText` (which dispatches
  // the typed command directly — no double-resolution). Null → falls through.
  const vpclipCommand = parseVpclipCommandText(command.text, command.source);
  if (vpclipCommand) {
    return vpclipCommand as ReturnType<typeof resolveGeometryDocumentCommand>;
  }

  return resolveGeometryCommandFromText(state, command.text, command.source)
    .command;
}
